/**
 * Activity Signals Service
 *
 * Frontend service for recording user activity signals (feedback, saves, views)
 * and retrieving learned preferences for personalization.
 */

import AuthService from './authService';
import { API_BASE as API_ROOT } from '../config/api';

const API_BASE = `${API_ROOT}/api/signals`;

// Batching configuration for low-priority signals (views, clicks)
const BATCH_FLUSH_INTERVAL_MS = 5000; // Flush every 5 seconds
const BATCH_MAX_SIZE = 30; // Flush if queue reaches this size

class ActivitySignalsService {
  // Signal batch queue and flush timer
  static _signalQueue = [];
  static _flushTimer = null;

  /**
   * Add a signal to the batch queue instead of sending immediately.
   * Flushes automatically every BATCH_FLUSH_INTERVAL_MS or when the queue is full.
   */
  static _enqueueSignal(signal) {
    this._signalQueue.push(signal);

    // Flush immediately if queue is full
    if (this._signalQueue.length >= BATCH_MAX_SIZE) {
      this._flushSignalQueue();
      return;
    }

    // Start a flush timer if not already running
    if (!this._flushTimer) {
      this._flushTimer = setTimeout(() => this._flushSignalQueue(), BATCH_FLUSH_INTERVAL_MS);
    }
  }

  /**
   * Flush all queued signals to the server in a single batch request.
   */
  static async _flushSignalQueue() {
    if (this._flushTimer) {
      clearTimeout(this._flushTimer);
      this._flushTimer = null;
    }

    if (this._signalQueue.length === 0) return;

    // Drain the queue
    const signals = this._signalQueue.splice(0);

    try {
      const response = await fetch(`${API_BASE}/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...AuthService.getAuthHeaders()
        },
        body: JSON.stringify({ signals })
      });
      if (!response.ok) {
        // Server error — store locally for later sync
        signals.forEach(s => this.storeLocalSignal(s));
      }
    } catch (error) {
      // Network error — store locally for later sync
      signals.forEach(s => this.storeLocalSignal(s));
    }
  }

  /**
   * Check if user is authenticated (sync check)
   */
  static isAuthenticated() {
    return AuthService.isAuthenticated();
  }

  /**
   * Get the current user ID from the auth token
   * Decodes the JWT to get the user ID without an async call
   */
  static getCurrentUserId() {
    if (!this.isAuthenticated()) {
      return null;
    }

    // Try to decode user ID from JWT token
    const token = AuthService.getAuthToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id || payload.sub || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Record a feedback signal (thumbs up/down)
   */
  static async recordFeedback(activity, signalValue, options = {}) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      // Not authenticated - store locally for later sync
      this.storeLocalSignal({ activity, signalValue, options, timestamp: Date.now() });
      return { success: true, local: true };
    }

    const signal = {
      userId,
      contentId: activity.id || activity.place_id,
      contentType: activity.type || 'place',
      signalType: 'feedback',
      signalValue, // 1 = positive, -1 = negative
      feedbackReason: options.reason || null,
      feedbackContext: options.context || null,
      activityTypes: activity.types || activity.googleData?.types || [],
      activityRating: activity.rating || activity.googleData?.rating || null,
      activityPriceLevel: activity.price_level || activity.googleData?.price_level || null,
      activityLocation: options.searchLocation || null,
      searchQuery: options.searchQuery || null,
      searchLocation: options.searchLocation || null,
      searchFilters: options.searchFilters || null,
      groupComposition: options.groupComposition || null,
      matchScore: activity.matchScore || options.matchScore || null,
      source: options.source || 'search_results'
    };

    return this.recordSignal(signal);
  }

  /**
   * Record a positive feedback (thumbs up)
   */
  static async recordPositiveFeedback(activity, options = {}) {
    return this.recordFeedback(activity, 1, options);
  }

  /**
   * Record a negative feedback (thumbs down)
   */
  static async recordNegativeFeedback(activity, options = {}) {
    return this.recordFeedback(activity, -1, options);
  }

  /**
   * Undo a feedback signal
   */
  static async undoFeedback(activity) {
    if (!this.isAuthenticated()) {
      return { success: true, local: true };
    }

    const userId = this.getCurrentUserId();
    const contentId = activity.id || activity.place_id;

    try {
      const response = await fetch(API_BASE, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...AuthService.getAuthHeaders()
        },
        body: JSON.stringify({
          userId,
          contentId,
          signalType: 'feedback'
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error undoing feedback:', error);
      // Return success anyway for optimistic UI
      return { success: true, local: true };
    }
  }

  /**
   * Record any type of signal
   */
  static async recordSignal(signal) {
    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...AuthService.getAuthHeaders()
        },
        body: JSON.stringify(signal)
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error recording signal:', error);
      // Store locally for later sync
      this.storeLocalSignal(signal);
      return { success: true, local: true };
    }
  }

  /**
   * Store signal locally when offline
   */
  static storeLocalSignal(signal) {
    const pending = JSON.parse(localStorage.getItem('pendingSignals') || '[]');
    pending.push({ ...signal, timestamp: Date.now() });
    localStorage.setItem('pendingSignals', JSON.stringify(pending));
  }

  /**
   * Sync pending local signals to server
   */
  static async syncPendingSignals() {
    if (!this.isAuthenticated()) {
      return { synced: 0, message: 'Not authenticated' };
    }

    const pending = JSON.parse(localStorage.getItem('pendingSignals') || '[]');
    if (pending.length === 0) return { synced: 0 };

    let synced = 0;
    const failed = [];

    for (const signal of pending) {
      try {
        const response = await fetch(API_BASE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...AuthService.getAuthHeaders()
          },
          body: JSON.stringify(signal)
        });

        if (response.ok) {
          synced++;
        } else {
          failed.push(signal);
        }
      } catch {
        failed.push(signal);
      }
    }

    localStorage.setItem('pendingSignals', JSON.stringify(failed));
    return { synced, failed: failed.length };
  }

  /**
   * Get feedback reasons from server
   */
  static async getFeedbackReasons(category = null) {
    // Return defaults if not authenticated - no point in making the request
    if (!this.isAuthenticated()) {
      return this.getDefaultReasons(category);
    }

    try {
      const url = category
        ? `${API_BASE}/feedback-reasons?category=${category}`
        : `${API_BASE}/feedback-reasons`;

      const response = await fetch(url, {
        headers: AuthService.getAuthHeaders()
      });

      const data = await response.json();
      return data.success ? data.reasons : this.getDefaultReasons(category);
    } catch (error) {
      console.error('Error fetching feedback reasons:', error);
      return this.getDefaultReasons(category);
    }
  }

  /**
   * Default feedback reasons (fallback)
   */
  static getDefaultReasons(category = null) {
    const allReasons = [
      // Negative
      { id: 'too_expensive', display_text: 'Too expensive', category: 'negative', emoji: '$' },
      { id: 'too_far', display_text: 'Too far away', category: 'negative', emoji: '??' },
      { id: 'wrong_vibe', display_text: 'Not the right vibe', category: 'negative', emoji: '??' },
      { id: 'been_there', display_text: 'Already been there', category: 'negative', emoji: '?' },
      { id: 'not_my_taste', display_text: 'Not my taste', category: 'negative', emoji: '??' },
      { id: 'not_kid_friendly', display_text: 'Not good for kids', category: 'negative', emoji: '??' },
      { id: 'bad_reviews', display_text: 'Saw bad reviews', category: 'negative', emoji: '?' },
      { id: 'timing', display_text: "Hours don't work", category: 'negative', emoji: '?' },
      // Positive
      { id: 'looks_great', display_text: 'Looks great!', category: 'positive', emoji: '?' },
      { id: 'good_value', display_text: 'Good value', category: 'positive', emoji: '??' },
      { id: 'perfect_location', display_text: 'Perfect location', category: 'positive', emoji: '??' },
      { id: 'kid_friendly', display_text: 'Great for kids', category: 'positive', emoji: '??' },
      { id: 'unique', display_text: 'Unique experience', category: 'positive', emoji: '?' }
    ];

    if (category) {
      return allReasons.filter(r => r.category === category);
    }
    return allReasons;
  }

  /**
   * Get user's feedback for specific content
   */
  static async getContentFeedback(contentId) {
    if (!this.isAuthenticated()) {
      return null;
    }

    const userId = this.getCurrentUserId();
    if (!userId) return null;

    try {
      const response = await fetch(
        `${API_BASE}/content/${contentId}?userId=${userId}`,
        { headers: AuthService.getAuthHeaders() }
      );

      const data = await response.json();
      if (data.success) {
        const feedback = data.signals.find(s => s.signal_type === 'feedback');
        return feedback ? feedback.signal_value : null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching content feedback:', error);
      return null;
    }
  }

  /**
   * Get user's learned preferences
   */
  static async getLearnedPreferences(type = null) {
    if (!this.isAuthenticated()) {
      return [];
    }

    try {
      const url = type
        ? `${API_BASE}/preferences?type=${type}`
        : `${API_BASE}/preferences`;

      const response = await fetch(url, {
        headers: AuthService.getAuthHeaders()
      });

      const data = await response.json();
      return data.success ? data.preferences : [];
    } catch (error) {
      console.error('Error fetching preferences:', error);
      return [];
    }
  }

  /**
   * Get signal statistics for the user
   */
  static async getSignalStats() {
    if (!this.isAuthenticated()) {
      return null;
    }

    const userId = this.getCurrentUserId();
    if (!userId) return null;

    try {
      const response = await fetch(
        `${API_BASE}/stats/${userId}`,
        { headers: AuthService.getAuthHeaders() }
      );

      const data = await response.json();
      return data.success ? data.stats : null;
    } catch (error) {
      console.error('Error fetching stats:', error);
      return null;
    }
  }

  /**
   * Check if user has given feedback on an activity
   * Returns: 1 (positive), -1 (negative), or null (no feedback)
   */
  static getFeedbackFromCache(activityId, feedbackCache) {
    if (!feedbackCache) return null;
    return feedbackCache[activityId] || null;
  }

  /**
   * Record a view/impression signal (when content is shown to user)
   * Batched — signals are queued and sent in bulk every 5 seconds.
   * @param {Object} content - The content that was viewed
   * @param {Object} options - Additional context (position, source, etc.)
   */
  static async recordView(content, options = {}) {
    const userId = this.getCurrentUserId();
    if (!userId) return { success: false, reason: 'not_authenticated' };

    const signal = {
      userId,
      contentId: content.id || content.place_id || content.content_id,
      contentType: content.content_type || content.type || 'place',
      signalType: 'view',
      signalValue: 0, // Neutral - just an impression
      activityTypes: content.types || content.googleData?.types || [],
      activityRating: content.rating || content.googleData?.rating || null,
      activityPriceLevel: content.price_level || content.googleData?.price_level || null,
      source: options.source || 'discover_feed',
      feedbackContext: {
        position: options.position,
        timestamp: Date.now()
      }
    };

    this._enqueueSignal(signal);
    return { success: true, batched: true };
  }

  /**
   * Record a click signal (when user clicks on content)
   * Batched — signals are queued and sent in bulk every 5 seconds.
   * @param {Object} content - The content that was clicked
   * @param {Object} options - Additional context (position, source, etc.)
   */
  static async recordClick(content, options = {}) {
    const userId = this.getCurrentUserId();
    if (!userId) return { success: false, reason: 'not_authenticated' };

    const signal = {
      userId,
      contentId: content.id || content.place_id || content.content_id,
      contentType: content.content_type || content.type || 'place',
      signalType: 'click',
      signalValue: 1, // Positive - user showed interest
      activityTypes: content.types || content.googleData?.types || [],
      activityRating: content.rating || content.googleData?.rating || null,
      activityPriceLevel: content.price_level || content.googleData?.price_level || null,
      source: options.source || 'discover_feed',
      feedbackContext: {
        position: options.position,
        timestamp: Date.now()
      }
    };

    this._enqueueSignal(signal);
    return { success: true, batched: true };
  }

  /**
   * Batch record multiple view signals (for efficiency)
   * @param {Array} contents - Array of content objects that were viewed
   * @param {string} source - Where the views occurred
   */
  static async recordBatchViews(contents, source = 'discover_feed') {
    const userId = this.getCurrentUserId();
    if (!userId || !contents.length) return { success: false };

    // Record views in parallel (fire and forget for performance)
    const promises = contents.map((content, index) =>
      this.recordView(content, { position: index, source }).catch(() => null)
    );

    // Don't wait for all to complete - return immediately
    Promise.all(promises).catch(console.error);
    return { success: true, queued: contents.length };
  }

  /**
   * Get feedback for multiple content IDs in a single request
   * Solves the N+1 query problem when scoring many activities
   *
   * @param {string[]} contentIds - Array of content IDs to look up
   * @returns {Object} Map of contentId -> signal_value (1, -1, or undefined)
   */
  static async getBatchContentFeedback(contentIds) {
    if (!contentIds || contentIds.length === 0) {
      return {};
    }

    if (!this.isAuthenticated()) {
      return {};
    }

    const userId = this.getCurrentUserId();
    if (!userId) return {};

    try {
      const response = await fetch(`${API_BASE}/batch-content-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...AuthService.getAuthHeaders()
        },
        body: JSON.stringify({ userId, contentIds })
      });

      const data = await response.json();
      return data.success ? data.feedback : {};
    } catch (error) {
      console.error('Error fetching batch content feedback:', error);
      return {};
    }
  }
}

// Flush queued signals when the user navigates away or the tab becomes hidden
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    ActivitySignalsService._flushSignalQueue();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      ActivitySignalsService._flushSignalQueue();
    }
  });
}

export default ActivitySignalsService;
